import React from "react";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
  EXPRESS_CLOSED_HINT,
  SAME_FEE_NOTE,
  SCHEDULED_ONLY_NOTICE,
} from "../utils/deliverySchedule";

/**
 * DeliveryTimePicker: Express or a 30 minute window, on every web checkout,
 * above payment. All state lives in useCheckoutDeliverySchedule; pass its
 * return value as `controller`. The dialog below is the one place the site asks
 * a customer to pick a new time.
 */
const DeliveryTimePicker = ({ controller }) => {
  if (!controller) return null;
  const {
    slots,
    query,
    pickerRef,
    scheduled,
    expressAvailable,
    canSchedule,
    closedNotAccepting,
    scheduledOnly,
    selectedDate,
    selectedStart,
    activeDay,
    activeWindow,
    needsWindow,
    prompt,
  } = controller;

  const loading = slots.status === "loading";
  // A call that failed against a server that HAS the endpoint: retryable, and
  // worth saying out loud.
  const failed = slots.status === "error";
  // This deployment does not serve delivery windows yet. Scheduling is simply
  // not on offer, which is neither an error nor something a customer can retry
  // away: the Scheduled card is disabled with plain copy and nothing shouts.
  const unsupported = slots.status === "unsupported";
  const empty = slots.status === "ready" && !canSchedule;

  const scheduleHint = !query || unsupported
    ? "Not available for this order"
    : loading
      ? "Checking available times"
      : failed
        ? "Times could not load"
        : empty
          ? "No times left in the next 3 days"
          : "Pick a 30 minute window, up to 2 days ahead";

  return (
    <Panel ref={pickerRef} aria-labelledby="delivery-time-title">
      <Body>
        <Title id="delivery-time-title">Delivery time</Title>
        <Note>{SAME_FEE_NOTE}</Note>

        {scheduledOnly && <Notice role="status">{SCHEDULED_ONLY_NOTICE}</Notice>}
        {closedNotAccepting && (
          <ErrorBox role="alert">
            {slots.message || "This shop is not taking orders right now."}
          </ErrorBox>
        )}

        <Options role="radiogroup" aria-label="Delivery time">
          <OptionCard
            type="button"
            role="radio"
            aria-checked={!scheduled}
            $selected={!scheduled}
            disabled={!expressAvailable}
            onClick={controller.selectExpress}
          >
            <OptionIcon $tint="#03660c">
              <FlashOnIcon style={{ fontSize: 22 }} />
            </OptionIcon>
            <OptionText>
              <OptionTitle>Express delivery</OptionTitle>
              <OptionHint>
                {expressAvailable ? "As soon as your order is ready" : EXPRESS_CLOSED_HINT}
              </OptionHint>
            </OptionText>
            <Radio $selected={!scheduled} aria-hidden="true" />
          </OptionCard>

          <OptionCard
            type="button"
            role="radio"
            aria-checked={scheduled}
            $selected={scheduled}
            disabled={!query || unsupported || (!canSchedule && !scheduled)}
            onClick={controller.selectScheduled}
          >
            <OptionIcon $tint="#1d4ed8">
              <ScheduleIcon style={{ fontSize: 22 }} />
            </OptionIcon>
            <OptionText>
              <OptionTitle>Schedule for later</OptionTitle>
              <OptionHint>{scheduleHint}</OptionHint>
            </OptionText>
            <Radio $selected={scheduled} aria-hidden="true" />
          </OptionCard>
        </Options>

        {query && loading && (
          <Status role="status">
            <CircularProgress size={16} style={{ color: "#03660c" }} />
            <span>Checking available times</span>
          </Status>
        )}

        {query && failed && (
          <ErrorBox role="alert">
            <span>{slots.error}</span>
            <LinkButton type="button" onClick={slots.refresh}>
              Try again
            </LinkButton>
          </ErrorBox>
        )}

        {scheduled && empty && !closedNotAccepting && (
          <Status role="status">
            {slots.message || "No delivery times are left in the next 3 days."}
          </Status>
        )}

        {scheduled && canSchedule && (
          <>
            <DayRow>
              {slots.days.map((day) => {
                const active = day.date === selectedDate;
                const none = day.windows.length === 0;
                return (
                  <DayChip
                    key={day.date}
                    type="button"
                    disabled={none}
                    $active={active}
                    aria-pressed={active}
                    onClick={() => controller.selectDay(day.date)}
                  >
                    <span className="day">{day.label}</span>
                    <span className="meta">
                      {none ? "No times" : `${day.windows.length} times`}
                    </span>
                  </DayChip>
                );
              })}
            </DayRow>

            <WindowGrid role="radiogroup" aria-label="Delivery window">
              {(activeDay?.windows || []).map((window) => {
                const active = window.start === selectedStart;
                return (
                  <WindowChip
                    key={window.start}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    $active={active}
                    onClick={() => controller.selectWindow(window.start)}
                  >
                    {window.label}
                  </WindowChip>
                );
              })}
            </WindowGrid>
          </>
        )}

        {scheduled && activeWindow && (
          <Chosen>
            Delivering {activeDay.label}, {activeWindow.label}
          </Chosen>
        )}
        {needsWindow && canSchedule && <Hint>Pick a delivery time to continue.</Hint>}
      </Body>

      <Dialog
        open={Boolean(prompt)}
        onClose={controller.dismissPrompt}
        aria-labelledby="delivery-time-dialog-title"
        fullWidth
        maxWidth="xs"
        /* Above the quote-accept overlay (z-index 9999), which would otherwise
           cover this dialog: MUI's default is 1300. */
        style={{ zIndex: 12000 }}
      >
        <DialogTitle id="delivery-time-dialog-title">{prompt?.title}</DialogTitle>
        <DialogContent>
          <DialogText>{prompt?.message}</DialogText>
        </DialogContent>
        <DialogActions style={{ padding: "0 16px 16px", flexWrap: "wrap", gap: 8 }}>
          {prompt?.canPick && expressAvailable && (
            <SecondaryButton type="button" onClick={controller.switchToExpress}>
              Switch to express
            </SecondaryButton>
          )}
          {prompt?.canPick ? (
            <PrimaryButton type="button" onClick={controller.pickAnotherTime}>
              Pick another time
            </PrimaryButton>
          ) : (
            <PrimaryButton type="button" onClick={controller.dismissPrompt}>
              OK
            </PrimaryButton>
          )}
        </DialogActions>
      </Dialog>
    </Panel>
  );
};

export default DeliveryTimePicker;

// Visual language of the confirm pages (RestaurantPlaceOrderScreen): flat white
// panels, #03660c accent, 14px radius, scroll-margin for the fixed navbars.
const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e7ecec;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 42, 46, 0.06), 0 8px 24px rgba(16, 42, 46, 0.05);
  margin-top: 16px;
  scroll-margin-top: 120px;
  max-width: 100%;
  box-sizing: border-box;
`;

const Body = styled.div`
  padding: 14px 18px 18px;

  @media (max-width: 480px) {
    padding: 12px 12px 14px;
  }
`;

const Title = styled.h2`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #5a6b6d;
  margin: 0;
  padding: 2px 0 2px;
  font-weight: 700;
`;

const Note = styled.p`
  color: #5a6b6d;
  font-size: 13px;
  margin: 0 0 12px;
`;

const Notice = styled.div`
  padding: 10px 12px;
  border: 1px solid #bfe3cd;
  border-radius: 10px;
  background: #eef7f1;
  color: #0a5c40;
  font-size: 13.5px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
  text-align: left;
  padding: 12px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.$selected ? "#03660c" : "#e2e8f0")};
  background: ${(p) => (p.$selected ? "#f3faf5" : "#ffffff")};
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid #03660c;
    outline-offset: 2px;
  }
`;

const OptionIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex: none;
  color: ${(p) => p.$tint};
  background: ${(p) => `${p.$tint}1A`};
`;

const OptionText = styled.span`
  display: block;
  min-width: 0;
  flex: 1;
`;

const OptionTitle = styled.span`
  display: block;
  font-size: 14.5px;
  font-weight: 600;
  color: #0f2a2e;
`;

const OptionHint = styled.span`
  display: block;
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
`;

const Radio = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex: none;
  border: 2px solid ${(p) => (p.$selected ? "#03660c" : "#cbd5e1")};
  box-shadow: ${(p) => (p.$selected ? "inset 0 0 0 3px #ffffff" : "none")};
  background: ${(p) => (p.$selected ? "#03660c" : "#ffffff")};
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5a6b6d;
  font-size: 13px;
  margin-top: 12px;
`;

const ErrorBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #fdba74;
  border-radius: 10px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 13px;
  margin: 10px 0 0;
`;

const LinkButton = styled.button`
  border: 0;
  background: none;
  color: #03660c;
  font-weight: 700;
  font-size: 13.5px;
  cursor: pointer;
  padding: 2px 0;
  font-family: inherit;
`;

const DayRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 14px 0 6px;
  -webkit-overflow-scrolling: touch;
`;

const DayChip = styled.button`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 96px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$active ? "#03660c" : "#e2e8f0")};
  background: ${(p) => (p.$active ? "#03660c" : "#ffffff")};
  color: ${(p) => (p.$active ? "#ffffff" : "#0f2a2e")};
  cursor: pointer;
  font-family: inherit;

  .day {
    font-size: 14px;
    font-weight: 600;
  }
  .meta {
    font-size: 12px;
    opacity: 0.8;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const WindowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
  gap: 8px;
  margin-top: 8px;

  @media (max-width: 360px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const WindowChip = styled.button`
  padding: 9px 8px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$active ? "#03660c" : "#e2e8f0")};
  background: ${(p) => (p.$active ? "#eef7f1" : "#ffffff")};
  color: ${(p) => (p.$active ? "#0a5c40" : "#0f2a2e")};
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  font-family: inherit;
`;

const Chosen = styled.div`
  margin-top: 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: #0a5c40;
`;

const Hint = styled.div`
  margin-top: 10px;
  font-size: 12.5px;
  color: #9a3412;
`;

const DialogText = styled.p`
  margin: 0;
  color: #334155;
  font-size: 14px;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  border: 0;
  border-radius: 10px;
  background: #03660c;
  color: #ffffff;
  font-weight: 650;
  font-size: 14px;
  padding: 10px 16px;
  cursor: pointer;
  font-family: inherit;
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f2a2e;
  font-weight: 600;
  font-size: 14px;
  padding: 10px 16px;
  cursor: pointer;
  font-family: inherit;
`;
